/**
 * Futures (COIN-M) REST API
 *
 * Access market data, manage accounts, and trade COIN-M perpetual and delivery futures.
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { ConfigurationRestAPI, RestApiResponse, sendRequest } from '@binance/common';
import { AccountApi } from './modules/account-api';
import { MarketDataApi } from './modules/market-data-api';
import { TradeApi } from './modules/trade-api';
import { UserDataStreamsApi } from './modules/user-data-streams-api';

import type {
    AccountInformationRequest,
    FuturesAccountBalanceRequest,
    GetCurrentPositionModeRequest,
    GetDownloadIdForFuturesOrderHistoryRequest,
    GetDownloadIdForFuturesTradeHistoryRequest,
    GetDownloadIdForFuturesTransactionHistoryRequest,
    GetFuturesOrderHistoryDownloadLinkByIdRequest,
    GetFuturesTradeDownloadLinkByIdRequest,
    GetFuturesTransactionHistoryDownloadLinkByIdRequest,
    GetIncomeHistoryRequest,
    NotionalBracketForPairRequest,
    NotionalBracketForSymbolRequest,
    UserCommissionRateRequest,
} from './modules/account-api';
import type {
    BasisRequest,
    CompressedAggregateTradesListRequest,
    ContinuousContractKlineCandlestickDataRequest,
    GetFundingRateHistoryOfPerpetualFuturesRequest,
    IndexPriceAndMarkPriceRequest,
    IndexPriceKlineCandlestickDataRequest,
    KlineCandlestickDataRequest,
    LongShortRatioRequest,
    MarkPriceKlineCandlestickDataRequest,
    OldTradesLookupRequest,
    OpenInterestRequest,
    OpenInterestStatisticsRequest,
    OrderBookRequest,
    PremiumIndexKlineDataRequest,
    QueryIndexPriceConstituentsRequest,
    RecentTradesListRequest,
    SymbolOrderBookTickerRequest,
    SymbolPriceTickerRequest,
    TakerBuySellVolumeRequest,
    Ticker24hrPriceChangeStatisticsRequest,
    TopTraderLongShortRatioAccountsRequest,
    TopTraderLongShortRatioPositionsRequest,
} from './modules/market-data-api';
import type {
    AccountTradeListRequest,
    AllOrdersRequest,
    AutoCancelAllOpenOrdersRequest,
    CancelAllOpenOrdersRequest,
    CancelMultipleOrdersRequest,
    CancelOrderRequest,
    ChangeInitialLeverageRequest,
    ChangeMarginTypeRequest,
    ChangePositionModeRequest,
    CurrentAllOpenOrdersRequest,
    GetOrderModifyHistoryRequest,
    GetPositionMarginChangeHistoryRequest,
    ModifyIsolatedPositionMarginRequest,
    ModifyMultipleOrdersRequest,
    ModifyOrderRequest,
    NewOrderRequest,
    PlaceMultipleOrdersRequest,
    PositionAdlQuantileEstimationRequest,
    PositionInformationRequest,
    QueryCurrentOpenOrderRequest,
    QueryOrderRequest,
    UsersForceOrdersRequest,
} from './modules/trade-api';
import type {} from './modules/user-data-streams-api';

import type {
    AccountInformationResponse,
    FuturesAccountBalanceResponse,
    GetCurrentPositionModeResponse,
    GetDownloadIdForFuturesOrderHistoryResponse,
    GetDownloadIdForFuturesTradeHistoryResponse,
    GetDownloadIdForFuturesTransactionHistoryResponse,
    GetFuturesOrderHistoryDownloadLinkByIdResponse,
    GetFuturesTradeDownloadLinkByIdResponse,
    GetFuturesTransactionHistoryDownloadLinkByIdResponse,
    GetIncomeHistoryResponse,
    NotionalBracketForPairResponse,
    NotionalBracketForSymbolResponse,
    UserCommissionRateResponse,
} from './types';
import type {
    BasisResponse,
    CheckServerTimeResponse,
    CompressedAggregateTradesListResponse,
    ContinuousContractKlineCandlestickDataResponse,
    ExchangeInformationResponse,
    GetFundingRateHistoryOfPerpetualFuturesResponse,
    GetFundingRateInfoResponse,
    IndexPriceAndMarkPriceResponse,
    IndexPriceKlineCandlestickDataResponse,
    KlineCandlestickDataResponse,
    LongShortRatioResponse,
    MarkPriceKlineCandlestickDataResponse,
    OldTradesLookupResponse,
    OpenInterestResponse,
    OpenInterestStatisticsResponse,
    OrderBookResponse,
    PremiumIndexKlineDataResponse,
    QueryIndexPriceConstituentsResponse,
    RecentTradesListResponse,
    SymbolOrderBookTickerResponse,
    SymbolPriceTickerResponse,
    TakerBuySellVolumeResponse,
    Ticker24hrPriceChangeStatisticsResponse,
    TopTraderLongShortRatioAccountsResponse,
    TopTraderLongShortRatioPositionsResponse,
} from './types';
import type {
    AccountTradeListResponse,
    AllOrdersResponse,
    AutoCancelAllOpenOrdersResponse,
    CancelAllOpenOrdersResponse,
    CancelMultipleOrdersResponse,
    CancelOrderResponse,
    ChangeInitialLeverageResponse,
    ChangeMarginTypeResponse,
    ChangePositionModeResponse,
    CurrentAllOpenOrdersResponse,
    GetOrderModifyHistoryResponse,
    GetPositionMarginChangeHistoryResponse,
    ModifyIsolatedPositionMarginResponse,
    ModifyMultipleOrdersResponse,
    ModifyOrderResponse,
    NewOrderResponse,
    PlaceMultipleOrdersResponse,
    PositionAdlQuantileEstimationResponse,
    PositionInformationResponse,
    QueryCurrentOpenOrderResponse,
    QueryOrderResponse,
    UsersForceOrdersResponse,
} from './types';
import type { KeepaliveUserDataStreamResponse, StartUserDataStreamResponse } from './types';

export class RestAPI {
    private configuration: ConfigurationRestAPI;
    private accountApi: AccountApi;
    private marketDataApi: MarketDataApi;
    private tradeApi: TradeApi;
    private userDataStreamsApi: UserDataStreamsApi;

    constructor(configuration: ConfigurationRestAPI) {
        this.configuration = configuration;
        this.accountApi = new AccountApi(configuration);
        this.marketDataApi = new MarketDataApi(configuration);
        this.tradeApi = new TradeApi(configuration);
        this.userDataStreamsApi = new UserDataStreamsApi(configuration);
    }

    /**
     * Generic function to send a request.
     * @param endpoint - The API endpoint to call.
     * @param method - HTTP method to use (GET, POST, DELETE, etc.).
     * @param queryParams - Query parameters for the request.
     * @param bodyParams - Body parameters for the request.
     *
     * @returns A promise resolving to the response data object.
     */
    sendRequest<T>(
        endpoint: string,
        method: 'GET' | 'POST' | 'DELETE' | 'PUT' | 'PATCH',
        queryParams: Record<string, unknown> = {},
        bodyParams: Record<string, unknown> = {}
    ): Promise<RestApiResponse<T>> {
        return sendRequest<T>(
            this.configuration,
            endpoint,
            method,
            queryParams,
            bodyParams,
            undefined,
            undefined
        );
    }

    /**
     * Generic function to send a signed request.
     * @param endpoint - The API endpoint to call.
     * @param method - HTTP method to use (GET, POST, DELETE, etc.).
     * @param queryParams - Query parameters for the request.
     * @param bodyParams - Body parameters for the request.
     *
     * @returns A promise resolving to the response data object.
     */
    sendSignedRequest<T>(
        endpoint: string,
        method: 'GET' | 'POST' | 'DELETE' | 'PUT' | 'PATCH',
        queryParams: Record<string, unknown> = {},
        bodyParams: Record<string, unknown> = {}
    ): Promise<RestApiResponse<T>> {
        return sendRequest<T>(
            this.configuration,
            endpoint,
            method,
            queryParams,
            bodyParams,
            undefined,
            undefined,
            { isSigned: true }
        );
    }

    /**
     * Get current account information.
     *
     * Weight(IP): 5
     *
     * Security Type: USER_DATA
     *
     * Notes:
     * - for One-way Mode user, the "positions" will only show the "BOTH" positions
     * - for Hedge Mode user, the "positions" will show "BOTH", "LONG", and "SHORT" positions.
     *
     * @summary Account Information (USER_DATA)
     * @param {AccountInformationRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<AccountInformationResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/account#account-information Binance API Documentation}
     */
    accountInformation(
        requestParameters: AccountInformationRequest = {}
    ): Promise<RestApiResponse<AccountInformationResponse>> {
        return this.accountApi.accountInformation(requestParameters);
    }

    /**
     * Check futures account balance
     *
     * Weight(IP): 1
     *
     * Security Type: USER_DATA
     *
     * @summary Futures Account Balance (USER_DATA)
     * @param {FuturesAccountBalanceRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<FuturesAccountBalanceResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/account#futures-account-balance Binance API Documentation}
     */
    futuresAccountBalance(
        requestParameters: FuturesAccountBalanceRequest = {}
    ): Promise<RestApiResponse<FuturesAccountBalanceResponse>> {
        return this.accountApi.futuresAccountBalance(requestParameters);
    }

    /**
     * Get user's position mode (Hedge Mode or One-way Mode ) on ***EVERY symbol***
     *
     * Weight(IP): 30
     *
     * Security Type: USER_DATA
     *
     * @summary Get Current Position Mode (USER_DATA)
     * @param {GetCurrentPositionModeRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<GetCurrentPositionModeResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/account#get-current-position-mode Binance API Documentation}
     */
    getCurrentPositionMode(
        requestParameters: GetCurrentPositionModeRequest = {}
    ): Promise<RestApiResponse<GetCurrentPositionModeResponse>> {
        return this.accountApi.getCurrentPositionMode(requestParameters);
    }

    /**
     * Get Download Id For Futures Order History
     *
     * Weight(IP): 1000
     *
     * Security Type: USER_DATA
     *
     * Notes:
     * - Request Limitation is 8 times per month, shared by front end download page and rest api
     * - This endpoint uses the IP rate limit bucket and costs 1000 weight per call. The maximum is 2 calls per minute; the 3rd call within the same minute will trigger a ban.
     * - The time between `startTime` and `endTime` can not be longer than 1 year
     *
     * @summary Get Download Id For Futures Order History (USER_DATA)
     * @param {GetDownloadIdForFuturesOrderHistoryRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<GetDownloadIdForFuturesOrderHistoryResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/account#get-download-id-for-futures-order-history Binance API Documentation}
     */
    getDownloadIdForFuturesOrderHistory(
        requestParameters: GetDownloadIdForFuturesOrderHistoryRequest
    ): Promise<RestApiResponse<GetDownloadIdForFuturesOrderHistoryResponse>> {
        return this.accountApi.getDownloadIdForFuturesOrderHistory(requestParameters);
    }

    /**
     * Get download id for futures trade history
     *
     * Weight(IP): 1000
     *
     * Security Type: USER_DATA
     *
     * Notes:
     * - Request Limitation is 8 times per month, shared by front end download page and rest api
     * - This endpoint uses the IP rate limit bucket and costs 1000 weight per call. The maximum is 2 calls per minute; the 3rd call within the same minute will trigger a ban.
     * - The time between `startTime` and `endTime` can not be longer than 1 year
     *
     * @summary Get Download Id For Futures Trade History (USER_DATA)
     * @param {GetDownloadIdForFuturesTradeHistoryRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<GetDownloadIdForFuturesTradeHistoryResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/account#get-download-id-for-futures-trade-history Binance API Documentation}
     */
    getDownloadIdForFuturesTradeHistory(
        requestParameters: GetDownloadIdForFuturesTradeHistoryRequest
    ): Promise<RestApiResponse<GetDownloadIdForFuturesTradeHistoryResponse>> {
        return this.accountApi.getDownloadIdForFuturesTradeHistory(requestParameters);
    }

    /**
     * Get download id for futures transaction history
     *
     * Weight(IP): 1000
     *
     * Security Type: USER_DATA
     *
     * Notes:
     * - Request Limitation is 8 times per month, shared by front end download page and rest api
     * - This endpoint uses the IP rate limit bucket and costs 1000 weight per call. The maximum is 2 calls per minute; the 3rd call within the same minute will trigger a ban.
     * - The time between `startTime` and `endTime` can not be longer than 1 year
     *
     * @summary Get Download Id For Futures Transaction History (USER_DATA)
     * @param {GetDownloadIdForFuturesTransactionHistoryRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<GetDownloadIdForFuturesTransactionHistoryResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/account#get-download-id-for-futures-transaction-history Binance API Documentation}
     */
    getDownloadIdForFuturesTransactionHistory(
        requestParameters: GetDownloadIdForFuturesTransactionHistoryRequest
    ): Promise<RestApiResponse<GetDownloadIdForFuturesTransactionHistoryResponse>> {
        return this.accountApi.getDownloadIdForFuturesTransactionHistory(requestParameters);
    }

    /**
     * Get futures order history download link by Id
     *
     * Weight(IP): 5
     *
     * Security Type: USER_DATA
     *
     * Notes:
     * - Download link expiration: 7 days
     *
     * @summary Get Futures Order History Download Link by Id (USER_DATA)
     * @param {GetFuturesOrderHistoryDownloadLinkByIdRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<GetFuturesOrderHistoryDownloadLinkByIdResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/account#get-futures-order-history-download-link-by-id Binance API Documentation}
     */
    getFuturesOrderHistoryDownloadLinkById(
        requestParameters: GetFuturesOrderHistoryDownloadLinkByIdRequest
    ): Promise<RestApiResponse<GetFuturesOrderHistoryDownloadLinkByIdResponse>> {
        return this.accountApi.getFuturesOrderHistoryDownloadLinkById(requestParameters);
    }

    /**
     * Get futures trade download link by Id
     *
     * Weight(IP): 5
     *
     * Security Type: USER_DATA
     *
     * Notes:
     * - Download link expiration: 7 days
     *
     * @summary Get Futures Trade Download Link by Id (USER_DATA)
     * @param {GetFuturesTradeDownloadLinkByIdRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<GetFuturesTradeDownloadLinkByIdResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/account#get-futures-trade-download-link-by-id Binance API Documentation}
     */
    getFuturesTradeDownloadLinkById(
        requestParameters: GetFuturesTradeDownloadLinkByIdRequest
    ): Promise<RestApiResponse<GetFuturesTradeDownloadLinkByIdResponse>> {
        return this.accountApi.getFuturesTradeDownloadLinkById(requestParameters);
    }

    /**
     * Get futures transaction history download link by Id
     *
     * Weight(IP): 5
     *
     * Security Type: USER_DATA
     *
     * Notes:
     * - Download link expiration: 7 days
     *
     * @summary Get Futures Transaction History Download Link by Id (USER_DATA)
     * @param {GetFuturesTransactionHistoryDownloadLinkByIdRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<GetFuturesTransactionHistoryDownloadLinkByIdResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/account#get-futures-transaction-history-download-link-by-id Binance API Documentation}
     */
    getFuturesTransactionHistoryDownloadLinkById(
        requestParameters: GetFuturesTransactionHistoryDownloadLinkByIdRequest
    ): Promise<RestApiResponse<GetFuturesTransactionHistoryDownloadLinkByIdResponse>> {
        return this.accountApi.getFuturesTransactionHistoryDownloadLinkById(requestParameters);
    }

    /**
     * Get income history
     *
     * Weight(IP): 20
     *
     * Security Type: USER_DATA
     *
     * Notes:
     * - If `incomeType ` is not sent, all kinds of flow will be returned
     * - "trandId" is unique in the same "incomeType" for a user
     * - The time between `startTime` and `endTime` can not be longer than 1 year
     *
     * @summary Get Income History (USER_DATA)
     * @param {GetIncomeHistoryRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<GetIncomeHistoryResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/account#get-income-history Binance API Documentation}
     */
    getIncomeHistory(
        requestParameters: GetIncomeHistoryRequest = {}
    ): Promise<RestApiResponse<GetIncomeHistoryResponse>> {
        return this.accountApi.getIncomeHistory(requestParameters);
    }

    /**
     * **Not recommended to continue using this v1 endpoint**
     *
     * Get the pair's default notional bracket list, may return ambiguous
     * values when there have been multiple different `symbol` brackets under
     * the `pair`, suggest using the following `GET /dapi/v2/leverageBracket`
     * query instead to get the specific `symbol` notional bracket list.
     *
     * Weight(IP): 1
     *
     * Security Type: USER_DATA
     *
     * @summary Notional Bracket for Pair (USER_DATA)
     * @param {NotionalBracketForPairRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<NotionalBracketForPairResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/account#notional-bracket-for-pair Binance API Documentation}
     */
    notionalBracketForPair(
        requestParameters: NotionalBracketForPairRequest = {}
    ): Promise<RestApiResponse<NotionalBracketForPairResponse>> {
        return this.accountApi.notionalBracketForPair(requestParameters);
    }

    /**
     * Get the symbol's notional bracket list.
     *
     * Weight: 1 (after CM migration: 1 with `symbol` / 2 without `symbol`)
     *
     * Security Type: USER_DATA
     *
     * @summary Notional Bracket for Symbol (USER_DATA)
     * @param {NotionalBracketForSymbolRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<NotionalBracketForSymbolResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/account#notional-bracket-for-symbol Binance API Documentation}
     */
    notionalBracketForSymbol(
        requestParameters: NotionalBracketForSymbolRequest = {}
    ): Promise<RestApiResponse<NotionalBracketForSymbolResponse>> {
        return this.accountApi.notionalBracketForSymbol(requestParameters);
    }

    /**
     * Query user commission rate
     *
     * Weight(IP): 20
     *
     * Security Type: USER_DATA
     *
     * @summary User Commission Rate (USER_DATA)
     * @param {UserCommissionRateRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<UserCommissionRateResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/account#user-commission-rate Binance API Documentation}
     */
    userCommissionRate(
        requestParameters: UserCommissionRateRequest
    ): Promise<RestApiResponse<UserCommissionRateResponse>> {
        return this.accountApi.userCommissionRate(requestParameters);
    }

    /**
     * Query basis
     *
     * Weight(IP): 1
     *
     * Notes:
     * - If startTime and endTime are not sent, the most recent data is returned.
     * - Only the data of the latest 30 days is available.
     *
     * @summary Basis
     * @param {BasisRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<BasisResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/market-data#basis Binance API Documentation}
     */
    basis(requestParameters: BasisRequest): Promise<RestApiResponse<BasisResponse>> {
        return this.marketDataApi.basis(requestParameters);
    }

    /**
     * Test connectivity to the Rest API and get the current server time.
     *
     * Weight(IP): 1
     *
     * @summary Check Server time
     *
     * @returns {Promise<RestApiResponse<CheckServerTimeResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/market-data#check-server-time Binance API Documentation}
     */
    checkServerTime(): Promise<RestApiResponse<CheckServerTimeResponse>> {
        return this.marketDataApi.checkServerTime();
    }

    /**
     * Get compressed, aggregate trades. Market trades that fill in 100ms with
     * the same price and the same taking side will have the quantity
     * aggregated.
     *
     * Weight(IP): 20
     *
     * Notes:
     * - only trade histories within the past 48 hours (counted from now) can be queried
     * - If both `startTime` and `endTime` are sent, time between `startTime` and `endTime` must be less than 1 hour.
     * - If `fromId`, `startTime`, and `endTime` are not sent, the most recent aggregate trades will be returned.
     * - Only market trades will be aggregated and returned, which means the insurance fund trades and ADL trades won't be aggregated.
     * - Sending both `startTime`/`endTime` and `fromId` might cause response timeout, please send either `fromId` or `startTime`/`endTime`
     *
     * @summary Compressed/Aggregate Trades List
     * @param {CompressedAggregateTradesListRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<CompressedAggregateTradesListResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/market-data#compressed-aggregate-trades-list Binance API Documentation}
     */
    compressedAggregateTradesList(
        requestParameters: CompressedAggregateTradesListRequest
    ): Promise<RestApiResponse<CompressedAggregateTradesListResponse>> {
        return this.marketDataApi.compressedAggregateTradesList(requestParameters);
    }

    /**
     * Kline/candlestick bars for a specific contract type. Klines are uniquely identified by their open time.
     *
     * Weight: based on parameter `LIMIT`
     *
     * | LIMIT | weight |
     * | --- | --- |
     * | [1,100) | 1 |
     * | [100, 500) | 2 |
     * | [500, 1000] | 5 |
     * | > 1000 | 10 |
     *
     * Notes:
     * - The difference between `startTime` and `endTime` can only be up to 200 days
     * - Between `startTime` and `endTime`, the most recent `limit` data from `endTime` will be returned:
     * - If `startTime` and `endTime` are not sent, current timestamp will be set as `endTime`, and the most recent data will be returned.
     * - If `startTime` is sent only, the timestamp of 200 days after `startTime` will be set as `endTime`(up to the current time)
     * - If `endTime` is sent only, the timestamp of 200 days before `endTime` will be set as `startTime`
     *
     * @summary Continuous Contract Kline/Candlestick Data
     * @param {ContinuousContractKlineCandlestickDataRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<ContinuousContractKlineCandlestickDataResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/market-data#continuous-contract-kline-candlestick-data Binance API Documentation}
     */
    continuousContractKlineCandlestickData(
        requestParameters: ContinuousContractKlineCandlestickDataRequest
    ): Promise<RestApiResponse<ContinuousContractKlineCandlestickDataResponse>> {
        return this.marketDataApi.continuousContractKlineCandlestickData(requestParameters);
    }

    /**
     * Current exchange trading rules and symbol information
     *
     * Weight(IP): 1
     *
     * @summary Exchange Information
     *
     * @returns {Promise<RestApiResponse<ExchangeInformationResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/market-data#exchange-information Binance API Documentation}
     */
    exchangeInformation(): Promise<RestApiResponse<ExchangeInformationResponse>> {
        return this.marketDataApi.exchangeInformation();
    }

    /**
     * Get Funding Rate History of Perpetual Futures
     *
     * Weight(IP): 1
     *
     * Notes:
     * - empty array will be returned for delivery symbols.
     *
     * @summary Get Funding Rate History of Perpetual Futures
     * @param {GetFundingRateHistoryOfPerpetualFuturesRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<GetFundingRateHistoryOfPerpetualFuturesResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/market-data#get-funding-rate-history-of-perpetual-futures Binance API Documentation}
     */
    getFundingRateHistoryOfPerpetualFutures(
        requestParameters: GetFundingRateHistoryOfPerpetualFuturesRequest
    ): Promise<RestApiResponse<GetFundingRateHistoryOfPerpetualFuturesResponse>> {
        return this.marketDataApi.getFundingRateHistoryOfPerpetualFutures(requestParameters);
    }

    /**
     * Query funding rate info for symbols that had FundingRateCap/FundingRateFloor/fundingIntervalHours adjustment
     *
     * @summary Get Funding Rate Info
     *
     * @returns {Promise<RestApiResponse<GetFundingRateInfoResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/market-data#get-funding-rate-info Binance API Documentation}
     */
    getFundingRateInfo(): Promise<RestApiResponse<GetFundingRateInfoResponse>> {
        return this.marketDataApi.getFundingRateInfo();
    }

    /**
     * Query index price and mark price
     *
     * Weight(IP): 10
     *
     * @summary Index Price and Mark Price
     * @param {IndexPriceAndMarkPriceRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<IndexPriceAndMarkPriceResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/market-data#index-price-and-mark-price Binance API Documentation}
     */
    indexPriceAndMarkPrice(
        requestParameters: IndexPriceAndMarkPriceRequest = {}
    ): Promise<RestApiResponse<IndexPriceAndMarkPriceResponse>> {
        return this.marketDataApi.indexPriceAndMarkPrice(requestParameters);
    }

    /**
     * Kline/candlestick bars for the index price of a pair. Klines are uniquely identified by their open time.
     *
     * Weight: Based on parameter `LIMIT`
     *
     * | LIMIT | weight |
     * | --- | --- |
     * | [1,100) | 1 |
     * | [100, 500) | 2 |
     * | [500, 1000] | 5 |
     * | > 1000 | 10 |
     *
     * Notes:
     * - The difference between `startTime` and `endTime` can only be up to 200 days
     * - Between `startTime` and `endTime`, the most recent `limit` data from `endTime` will be returned:
     * - If `startTime` and `endTime` are not sent, current timestamp will be set as `endTime`, and the most recent data will be returned.
     * - If `startTime` is sent only, the timestamp of 200 days after `startTime` will be set as `endTime`(up to the current time)
     * - If `endTime` is sent only, the timestamp of 200 days before `endTime` will be set as `startTime`
     *
     * @summary Index Price Kline/Candlestick Data
     * @param {IndexPriceKlineCandlestickDataRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<IndexPriceKlineCandlestickDataResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/market-data#index-price-kline-candlestick-data Binance API Documentation}
     */
    indexPriceKlineCandlestickData(
        requestParameters: IndexPriceKlineCandlestickDataRequest
    ): Promise<RestApiResponse<IndexPriceKlineCandlestickDataResponse>> {
        return this.marketDataApi.indexPriceKlineCandlestickData(requestParameters);
    }

    /**
     * Kline/candlestick bars for a symbol. Klines are uniquely identified by their open time.
     *
     * Weight: Based on parameter `LIMIT`
     *
     * | LIMIT | weight |
     * | --- | --- |
     * | [1,100) | 1 |
     * | [100, 500) | 2 |
     * | [500, 1000] | 5 |
     * | > 1000 | 10 |
     *
     * Notes:
     * - The difference between `startTime` and `endTime` can only be up to 200 days
     * - Between `startTime` and `endTime`, the most recent `limit` data from `endTime` will be returned:
     * - If `startTime` and `endTime` are not sent, current timestamp will be set as `endTime`, and the most recent data will be returned.
     * - If `startTime` is sent only, the timestamp of 200 days after `startTime` will be set as `endTime`(up to the current time)
     * - If `endTime` is sent only, the timestamp of 200 days before `endTime` will be set as `startTime`
     *
     * @summary Kline/Candlestick Data
     * @param {KlineCandlestickDataRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<KlineCandlestickDataResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/market-data#kline-candlestick-data Binance API Documentation}
     */
    klineCandlestickData(
        requestParameters: KlineCandlestickDataRequest
    ): Promise<RestApiResponse<KlineCandlestickDataResponse>> {
        return this.marketDataApi.klineCandlestickData(requestParameters);
    }

    /**
     * Query symbol Long/Short Ratio
     *
     * Weight(IP): 1
     *
     * Notes:
     * - If startTime and endTime are not sent, the most recent data is returned.
     * - Only the data of the latest 30 days is available.
     *
     * @summary Long/Short Ratio
     * @param {LongShortRatioRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<LongShortRatioResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/market-data#long-short-ratio Binance API Documentation}
     */
    longShortRatio(
        requestParameters: LongShortRatioRequest
    ): Promise<RestApiResponse<LongShortRatioResponse>> {
        return this.marketDataApi.longShortRatio(requestParameters);
    }

    /**
     * Kline/candlestick bars for the mark price of a symbol. Klines are uniquely identified by their open time.
     *
     * Weight: Based on parameter `LIMIT`
     *
     * | LIMIT | weight |
     * | --- | --- |
     * | [1,100) | 1 |
     * | [100, 500) | 2 |
     * | [500, 1000] | 5 |
     * | > 1000 | 10 |
     *
     * Notes:
     * - The difference between `startTime` and `endTime` can only be up to 200 days
     * - Between `startTime` and `endTime`, the most recent `limit` data from `endTime` will be returned:
     * - If `startTime` and `endTime` are not sent, current timestamp will be set as `endTime`, and the most recent data will be returned.
     * - If `startTime` is sent only, the timestamp of 200 days after `startTime` will be set as `endTime`(up to the current time)
     * - If `endTime` is sent only, the timestamp of 200 days before `endTime` will be set as `startTime`
     *
     * @summary Mark Price Kline/Candlestick Data
     * @param {MarkPriceKlineCandlestickDataRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<MarkPriceKlineCandlestickDataResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/market-data#mark-price-kline-candlestick-data Binance API Documentation}
     */
    markPriceKlineCandlestickData(
        requestParameters: MarkPriceKlineCandlestickDataRequest
    ): Promise<RestApiResponse<MarkPriceKlineCandlestickDataResponse>> {
        return this.marketDataApi.markPriceKlineCandlestickData(requestParameters);
    }

    /**
     * Get older market historical trades.
     *
     * Weight(IP): 200
     *
     * Security Type: MARKET_DATA
     *
     * Notes:
     * - Market trades means trades filled in the order book. Only market trades will be returned, which means the insurance fund trades and ADL trades won't be returned.
     * - Only supports data from within the last one month
     *
     * @summary Old Trades Lookup (MARKET_DATA)
     * @param {OldTradesLookupRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<OldTradesLookupResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/market-data#old-trades-lookup Binance API Documentation}
     */
    oldTradesLookup(
        requestParameters: OldTradesLookupRequest
    ): Promise<RestApiResponse<OldTradesLookupResponse>> {
        return this.marketDataApi.oldTradesLookup(requestParameters);
    }

    /**
     * Get present open interest of a specific symbol.
     *
     * Weight(IP): 1
     *
     * @summary Open Interest
     * @param {OpenInterestRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<OpenInterestResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/market-data#open-interest Binance API Documentation}
     */
    openInterest(
        requestParameters: OpenInterestRequest
    ): Promise<RestApiResponse<OpenInterestResponse>> {
        return this.marketDataApi.openInterest(requestParameters);
    }

    /**
     * Query open interest stats
     *
     * Weight(IP): 1
     *
     * Notes:
     * - If startTime and endTime are not sent, the most recent data is returned.
     * - Only the data of the latest 30 days is available.
     *
     * @summary Open Interest Statistics
     * @param {OpenInterestStatisticsRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<OpenInterestStatisticsResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/market-data#open-interest-statistics Binance API Documentation}
     */
    openInterestStatistics(
        requestParameters: OpenInterestStatisticsRequest
    ): Promise<RestApiResponse<OpenInterestStatisticsResponse>> {
        return this.marketDataApi.openInterestStatistics(requestParameters);
    }

    /**
     * Query orderbook on specific symbol
     *
     * Weight: Adjusted based on the limit:
     *
     * | Limit | Weight |
     * | ------------ | ------------ |
     * | 5, 10, 20, 50 | 2 |
     * | 100 | 5 |
     * | 500 | 10 |
     * | 1000 | 20 |
     *
     * @summary Order Book
     * @param {OrderBookRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<OrderBookResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/market-data#order-book Binance API Documentation}
     */
    orderBook(requestParameters: OrderBookRequest): Promise<RestApiResponse<OrderBookResponse>> {
        return this.marketDataApi.orderBook(requestParameters);
    }

    /**
     * Premium index kline bars of a symbol. Klines are uniquely identified by their open time.
     *
     * Weight: Based on parameter `LIMIT`
     *
     * | LIMIT       | weight |
     * | ----------- | ------ |
     * | [1,100)     | 1      |
     * | [100, 500)  | 2      |
     * | [500, 1000] | 5      |
     * | > 1000      | 10     |
     *
     * Notes:
     * - If startTime and endTime are not sent, the most recent klines are returned.
     *
     * @summary Premium index Kline Data
     * @param {PremiumIndexKlineDataRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<PremiumIndexKlineDataResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/market-data#premium-index-kline-data Binance API Documentation}
     */
    premiumIndexKlineData(
        requestParameters: PremiumIndexKlineDataRequest
    ): Promise<RestApiResponse<PremiumIndexKlineDataResponse>> {
        return this.marketDataApi.premiumIndexKlineData(requestParameters);
    }

    /**
     * Query index price constituents
     *
     * Weight(IP): 1
     *
     * @summary Query Index Price Constituents
     * @param {QueryIndexPriceConstituentsRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<QueryIndexPriceConstituentsResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/market-data#query-index-price-constituents Binance API Documentation}
     */
    queryIndexPriceConstituents(
        requestParameters: QueryIndexPriceConstituentsRequest
    ): Promise<RestApiResponse<QueryIndexPriceConstituentsResponse>> {
        return this.marketDataApi.queryIndexPriceConstituents(requestParameters);
    }

    /**
     * Get recent market trades
     *
     * Weight(IP): 5
     *
     * Notes:
     * - Market trades means trades filled in the order book. Only market trades will be returned, which means the insurance fund trades and ADL trades won't be returned.
     *
     * @summary Recent Trades List
     * @param {RecentTradesListRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<RecentTradesListResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/market-data#recent-trades-list Binance API Documentation}
     */
    recentTradesList(
        requestParameters: RecentTradesListRequest
    ): Promise<RestApiResponse<RecentTradesListResponse>> {
        return this.marketDataApi.recentTradesList(requestParameters);
    }

    /**
     * Best price/qty on the order book for a symbol or symbols.
     *
     * Weight: **2** for a single symbol, **5** when the symbol parameter is omitted
     *
     * Notes:
     * - Symbol and pair cannot be sent together
     * - If a pair is sent,tickers for all symbols of the pair will be returned
     * - If either a pair or symbol is sent, tickers for all symbols of all pairs will be returned
     *
     * @summary Symbol Order Book Ticker
     * @param {SymbolOrderBookTickerRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<SymbolOrderBookTickerResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/market-data#symbol-order-book-ticker Binance API Documentation}
     */
    symbolOrderBookTicker(
        requestParameters: SymbolOrderBookTickerRequest = {}
    ): Promise<RestApiResponse<SymbolOrderBookTickerResponse>> {
        return this.marketDataApi.symbolOrderBookTicker(requestParameters);
    }

    /**
     * Latest price for a symbol or symbols.
     *
     * Weight: **1** for a single symbol, **2** when the symbol parameter is omitted
     *
     * Notes:
     * - Symbol and pair cannot be sent together
     * - If a pair is sent,tickers for all symbols of the pair will be returned
     * - If either a pair or symbol is sent, tickers for all symbols of all pairs will be returned
     *
     * @summary Symbol Price Ticker
     * @param {SymbolPriceTickerRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<SymbolPriceTickerResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/market-data#symbol-price-ticker Binance API Documentation}
     */
    symbolPriceTicker(
        requestParameters: SymbolPriceTickerRequest = {}
    ): Promise<RestApiResponse<SymbolPriceTickerResponse>> {
        return this.marketDataApi.symbolPriceTicker(requestParameters);
    }

    /**
     * Taker Buy Volume: the total volume of buy orders filled by takers within
     * the period.
     *
     * Taker Sell Volume: the total volume of sell orders filled by takers
     * within the period.
     *
     * Weight(IP): 1
     *
     * Notes:
     * - If startTime and endTime are not sent, the most recent data is returned.
     * - Only the data of the latest 30 days is available.
     *
     * @summary Taker Buy/Sell Volume
     * @param {TakerBuySellVolumeRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<TakerBuySellVolumeResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/market-data#taker-buy-sell-volume Binance API Documentation}
     */
    takerBuySellVolume(
        requestParameters: TakerBuySellVolumeRequest
    ): Promise<RestApiResponse<TakerBuySellVolumeResponse>> {
        return this.marketDataApi.takerBuySellVolume(requestParameters);
    }

    /**
     * Test connectivity to the Rest API.
     *
     * Weight(IP): 1
     *
     * @summary Test Connectivity
     *
     * @returns {Promise<RestApiResponse<void>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/market-data#test-connectivity Binance API Documentation}
     */
    testConnectivity(): Promise<RestApiResponse<void>> {
        return this.marketDataApi.testConnectivity();
    }

    /**
     * 24 hour rolling window price change statistics.
     *
     * Weight: **1** for a single symbol, **40** when the symbol parameter is omitted
     **Careful** when accessing this with no symbol.
     *
     * Notes:
     * - Symbol and pair cannot be sent together
     * - If a pair is sent,tickers for all symbols of the pair will be returned
     * - If either a pair or symbol is sent, tickers for all symbols of all pairs will be returned
     *
     * @summary 24hr Ticker Price Change Statistics
     * @param {Ticker24hrPriceChangeStatisticsRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<Ticker24hrPriceChangeStatisticsResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/market-data#ticker24hr-price-change-statistics Binance API Documentation}
     */
    ticker24hrPriceChangeStatistics(
        requestParameters: Ticker24hrPriceChangeStatisticsRequest = {}
    ): Promise<RestApiResponse<Ticker24hrPriceChangeStatisticsResponse>> {
        return this.marketDataApi.ticker24hrPriceChangeStatistics(requestParameters);
    }

    /**
     * The proportion of net long and net short accounts to total accounts of
     * the top 20% users with the highest margin balance. Each account is
     * counted once only.
     *
     * Long Account % = Accounts of top traders with net long positions / Total
     * accounts of top traders with open positions
     *
     * Short Account % = Accounts of top traders with net short positions /
     * Total accounts of top traders with open positions
     *
     * Long/Short Ratio (Accounts) = Long Account % / Short Account %
     *
     * Weight(IP): 1
     *
     * Security Type: Accounts
     *
     * Notes:
     * - If startTime and endTime are not sent, the most recent data is returned.
     * - Only the data of the latest 30 days is available.
     *
     * @summary Top Trader Long/Short Account Ratio
     * @param {TopTraderLongShortRatioAccountsRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<TopTraderLongShortRatioAccountsResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/market-data#top-trader-long-short-ratio-accounts Binance API Documentation}
     */
    topTraderLongShortRatioAccounts(
        requestParameters: TopTraderLongShortRatioAccountsRequest
    ): Promise<RestApiResponse<TopTraderLongShortRatioAccountsResponse>> {
        return this.marketDataApi.topTraderLongShortRatioAccounts(requestParameters);
    }

    /**
     * The proportion of net long and net short positions to total open
     * positions of the top 20% users with the highest margin balance.
     *
     * Long Position % = Long positions of top traders / Total open positions
     * of top traders
     *
     * Short Position % = Short positions of top traders / Total open positions
     * of top traders
     *
     * Long/Short Ratio (Positions) = Long Position % / Short Position %
     *
     * Weight(IP): 1
     *
     * Security Type: Positions
     *
     * Notes:
     * - If startTime and endTime are not sent, the most recent data is returned.
     * - Only the data of the latest 30 days is available.
     *
     * @summary Top Trader Long/Short Position Ratio
     * @param {TopTraderLongShortRatioPositionsRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<TopTraderLongShortRatioPositionsResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/market-data#top-trader-long-short-ratio-positions Binance API Documentation}
     */
    topTraderLongShortRatioPositions(
        requestParameters: TopTraderLongShortRatioPositionsRequest
    ): Promise<RestApiResponse<TopTraderLongShortRatioPositionsResponse>> {
        return this.marketDataApi.topTraderLongShortRatioPositions(requestParameters);
    }

    /**
     * Get trades for a specific account and symbol.
     *
     * Weight: **20** with symbol，**40** with pair (after CM migration: **5** flat)
     *
     * Security Type: USER_DATA
     *
     * Notes:
     * - Either symbol or pair must be sent
     * - Symbol and pair cannot be sent together
     * - Pair and fromId cannot be sent together
     * - OrderId can only be sent together with symbol
     * - If a pair is sent,tickers for all symbols of the pair will be returned
     * - The parameter `fromId` cannot be sent with `startTime` or `endTime`
     * - If startTime and endTime are both not sent, then the last 7 days' data will be returned.
     * - The time between startTime and endTime cannot be longer than 7 days.
     * - Only support querying trade in the past 3 months
     *
     * @summary Account Trade List (USER_DATA)
     * @param {AccountTradeListRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<AccountTradeListResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/trade#account-trade-list Binance API Documentation}
     */
    accountTradeList(
        requestParameters: AccountTradeListRequest = {}
    ): Promise<RestApiResponse<AccountTradeListResponse>> {
        return this.tradeApi.accountTradeList(requestParameters);
    }

    /**
     * Get all account orders; active, canceled, or filled.
     *
     * These orders will not be found:
     * order status is CANCELED or EXPIRED AND order has NO filled trade AND created time + 3 days < current time
     * order create time + 90 days < current time
     *
     * Weight: **20** with symbol, **40** with pair (after CM migration: **5** flat)
     *
     * Security Type: USER_DATA
     *
     * Notes:
     * - Either `symbol` or `pair` must be sent.
     * - `pair` can't be sent with `orderId`
     * - If `orderId` is set, it will get orders >= that `orderId`. Otherwise most recent orders are returned.
     * - If orderId is set, it will get orders >= that orderId. Otherwise most recent orders are returned.
     * - The query time period must be less then 7 days( default as the recent 7 days).
     *
     * @summary All Orders (USER_DATA)
     * @param {AllOrdersRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<AllOrdersResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/trade#all-orders Binance API Documentation}
     */
    allOrders(
        requestParameters: AllOrdersRequest = {}
    ): Promise<RestApiResponse<AllOrdersResponse>> {
        return this.tradeApi.allOrders(requestParameters);
    }

    /**
     * Cancel all open orders of the specified symbol at the end of the
     * specified countdown. This rest endpoint means to ensure your open orders
     * are canceled in case of an outage. The endpoint should be called
     * repeatedly as heartbeats so that the existing countdown time can be
     * canceled and repalced by a new one. The system will check all countdowns
     **approximately every 10 milliseconds**, so please note that sufficient
     * redundancy should be considered when using this function. We do not
     * recommend setting the countdown time to be too precise or too small.
     *
     *
     * Example usage:
     *
     * Call this endpoint at 30s intervals with an countdownTime of 120000
     * (120s).
     *
     * If this endpoint is not called within 120 seconds, all your orders of
     * the specified symbol will be automatically canceled.
     *
     * If this endpoint is called with an countdownTime of 0, the countdown
     * timer will be stopped.
     *
     * Weight(IP): 10
     *
     * Security Type: TRADE
     *
     * @summary Auto-Cancel All Open Orders (TRADE)
     * @param {AutoCancelAllOpenOrdersRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<AutoCancelAllOpenOrdersResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/trade#auto-cancel-all-open-orders Binance API Documentation}
     */
    autoCancelAllOpenOrders(
        requestParameters: AutoCancelAllOpenOrdersRequest
    ): Promise<RestApiResponse<AutoCancelAllOpenOrdersResponse>> {
        return this.tradeApi.autoCancelAllOpenOrders(requestParameters);
    }

    /**
     * Cancel All Open Orders
     *
     * Weight(IP): 1
     *
     * Security Type: TRADE
     *
     * @summary Cancel All Open Orders (TRADE)
     * @param {CancelAllOpenOrdersRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<CancelAllOpenOrdersResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/trade#cancel-all-open-orders Binance API Documentation}
     */
    cancelAllOpenOrders(
        requestParameters: CancelAllOpenOrdersRequest
    ): Promise<RestApiResponse<CancelAllOpenOrdersResponse>> {
        return this.tradeApi.cancelAllOpenOrders(requestParameters);
    }

    /**
     * Cancel Multiple Orders
     *
     * Weight(IP): 1
     *
     * Security Type: TRADE
     *
     * Notes:
     * - Either `orderIdList` or `origClientOrderIdList ` must be sent.
     *
     * @summary Cancel Multiple Orders (TRADE)
     * @param {CancelMultipleOrdersRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<CancelMultipleOrdersResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/trade#cancel-multiple-orders Binance API Documentation}
     */
    cancelMultipleOrders(
        requestParameters: CancelMultipleOrdersRequest
    ): Promise<RestApiResponse<CancelMultipleOrdersResponse>> {
        return this.tradeApi.cancelMultipleOrders(requestParameters);
    }

    /**
     * Cancel an active order.
     *
     * Weight(IP): 1
     *
     * Security Type: TRADE
     *
     * Notes:
     * - Either `orderId` or `origClientOrderId` must be sent.
     *
     * @summary Cancel Order (TRADE)
     * @param {CancelOrderRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<CancelOrderResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/trade#cancel-order Binance API Documentation}
     */
    cancelOrder(
        requestParameters: CancelOrderRequest
    ): Promise<RestApiResponse<CancelOrderResponse>> {
        return this.tradeApi.cancelOrder(requestParameters);
    }

    /**
     * Change user's initial leverage in the specific symbol market.
     *
     * For Hedge Mode, LONG and SHORT positions of one symbol use the same
     * initial leverage and share a total notional value.
     *
     * Weight(IP): 1
     *
     * Security Type: TRADE
     *
     * @summary Change Initial Leverage (TRADE)
     * @param {ChangeInitialLeverageRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<ChangeInitialLeverageResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/trade#change-initial-leverage Binance API Documentation}
     */
    changeInitialLeverage(
        requestParameters: ChangeInitialLeverageRequest
    ): Promise<RestApiResponse<ChangeInitialLeverageResponse>> {
        return this.tradeApi.changeInitialLeverage(requestParameters);
    }

    /**
     * Change user's margin type in the specific symbol market.For Hedge Mode,
     * LONG and SHORT positions of one symbol use the same margin type.
     *
     * With ISOLATED margin type, margins of the LONG and SHORT positions are
     * isolated from each other.
     *
     * Weight(IP): 1
     *
     * Security Type: TRADE
     *
     * @summary Change Margin Type (TRADE)
     * @param {ChangeMarginTypeRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<ChangeMarginTypeResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/trade#change-margin-type Binance API Documentation}
     */
    changeMarginType(
        requestParameters: ChangeMarginTypeRequest
    ): Promise<RestApiResponse<ChangeMarginTypeResponse>> {
        return this.tradeApi.changeMarginType(requestParameters);
    }

    /**
     * Change user's position mode (Hedge Mode or One-way Mode ) on ***EVERY symbol***.
     *
     **After CM migration**, UM and CM share the **same** `dualSidePosition` setting. Calling this endpoint flips both UM and CM at once. If either side has any open order or open position, the change is rejected:
     * - `-4067` (open orders exist)
     * - `-4068` (open position exists)
     *
     * Weight(IP): 1
     *
     * Security Type: TRADE
     *
     * @summary Change Position Mode (TRADE)
     * @param {ChangePositionModeRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<ChangePositionModeResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/trade#change-position-mode Binance API Documentation}
     */
    changePositionMode(
        requestParameters: ChangePositionModeRequest
    ): Promise<RestApiResponse<ChangePositionModeResponse>> {
        return this.tradeApi.changePositionMode(requestParameters);
    }

    /**
     * Get all open orders on a symbol. **Careful** when accessing this with no symbol.
     *
     * Weight(IP): null
     *
     * Weight: **1** for a single symbol, **40** for mutltiple symbols
     *
     * Security Type: USER_DATA
     *
     * @summary Current All Open Orders (USER_DATA)
     * @param {CurrentAllOpenOrdersRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<CurrentAllOpenOrdersResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/trade#current-all-open-orders Binance API Documentation}
     */
    currentAllOpenOrders(
        requestParameters: CurrentAllOpenOrdersRequest = {}
    ): Promise<RestApiResponse<CurrentAllOpenOrdersResponse>> {
        return this.tradeApi.currentAllOpenOrders(requestParameters);
    }

    /**
     * Get order modification history
     *
     * Weight(IP): 1
     *
     * Security Type: USER_DATA
     *
     * Notes:
     * - Either `orderId` or `origClientOrderId` must be sent, and the `orderId` will prevail if both are sent.
     * - Order modify history longer than 3 month is not avaliable
     *
     * @summary Get Order Modify History (USER_DATA)
     * @param {GetOrderModifyHistoryRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<GetOrderModifyHistoryResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/trade#get-order-modify-history Binance API Documentation}
     */
    getOrderModifyHistory(
        requestParameters: GetOrderModifyHistoryRequest
    ): Promise<RestApiResponse<GetOrderModifyHistoryResponse>> {
        return this.tradeApi.getOrderModifyHistory(requestParameters);
    }

    /**
     * Get position margin change history
     *
     * Weight(IP): 1
     *
     * Security Type: TRADE
     *
     * @summary Get Position Margin Change History (TRADE)
     * @param {GetPositionMarginChangeHistoryRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<GetPositionMarginChangeHistoryResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/trade#get-position-margin-change-history Binance API Documentation}
     */
    getPositionMarginChangeHistory(
        requestParameters: GetPositionMarginChangeHistoryRequest
    ): Promise<RestApiResponse<GetPositionMarginChangeHistoryResponse>> {
        return this.tradeApi.getPositionMarginChangeHistory(requestParameters);
    }

    /**
     * Modify Isolated Position Margin
     *
     * Weight(IP): 1
     *
     * Security Type: TRADE
     *
     * Notes:
     * - Only for isolated symbol
     *
     * @summary Modify Isolated Position Margin (TRADE)
     * @param {ModifyIsolatedPositionMarginRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<ModifyIsolatedPositionMarginResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/trade#modify-isolated-position-margin Binance API Documentation}
     */
    modifyIsolatedPositionMargin(
        requestParameters: ModifyIsolatedPositionMarginRequest
    ): Promise<RestApiResponse<ModifyIsolatedPositionMarginResponse>> {
        return this.tradeApi.modifyIsolatedPositionMargin(requestParameters);
    }

    /**
     * Modify Multiple Orders
     *
     * Weight(IP): 5
     *
     * Security Type: TRADE
     *
     * Notes:
     * - Parameter rules are same with `Modify Order`
     * - Batch modify orders are processed concurrently, and the order of matching is not guaranteed.
     * - The order of returned contents for batch modify orders is the same as the order of the order list.
     * - One order can only be modfied for less than 10000 times
     * - `modifyId` is an optional user-defined identifier passed through as-is; the server does not validate its uniqueness. If omitted, it is not included in the response.
     *
     * @summary Modify Multiple Orders (TRADE)
     * @param {ModifyMultipleOrdersRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<ModifyMultipleOrdersResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/trade#modify-multiple-orders Binance API Documentation}
     */
    modifyMultipleOrders(
        requestParameters: ModifyMultipleOrdersRequest
    ): Promise<RestApiResponse<ModifyMultipleOrdersResponse>> {
        return this.tradeApi.modifyMultipleOrders(requestParameters);
    }

    /**
     * Order modify function, currently only LIMIT order modification is supported, modified orders will be reordered in the match queue
     *
     * Weight(IP): 1
     *
     * Security Type: TRADE
     *
     * Notes:
     * - Either `orderId` or `origClientOrderId` must be sent, and the `orderId` will prevail if both are sent.
     * - Either `quantity` or `price` must be sent. *(After CM migration, both `quantity` and `price` are required.)*
     * - When the new `quantity` or `price` doesn't satisfy PRICE_FILTER / PERCENT_FILTER / LOT_SIZE, amendment will be rejected and the order will stay as it is.
     * - However the order will be cancelled by the amendment in the following situations:
     * - when the order is in partially filled status and the new `quantity` <= `executedQty`
     * - When the order is `GTX` and the new price will cause it to be executed immediately
     * - One order can only be modfied for less than 10000 times
     *
     * @summary Modify Order (TRADE)
     * @param {ModifyOrderRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<ModifyOrderResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/trade#modify-order Binance API Documentation}
     */
    modifyOrder(
        requestParameters: ModifyOrderRequest
    ): Promise<RestApiResponse<ModifyOrderResponse>> {
        return this.tradeApi.modifyOrder(requestParameters);
    }

    /**
     * Send in a new order.
     *
     * Weight: 1 on 1min order rate limit(X-MBX-ORDER-COUNT-1M)
     * 0 on IP rate limit(x-mbx-used-weight-1m)
     *
     * Security Type: TRADE
     *
     * Notes:
     * - Additional mandatory parameters based on `type`:
     * - Order with type `STOP`, parameter `timeInForce` can be sent ( default `GTC`).
     * - Order with type `TAKE_PROFIT`, parameter `timeInForce` can be sent ( default `GTC`).
     * - Condition orders will be triggered when:
     * - If parameter`priceProtect`is sent as true:
     * - when price reaches the `stopPrice` ，the difference rate between "MARK_PRICE" and "CONTRACT_PRICE" cannot be larger than the "triggerProtect" of the symbol
     * - "triggerProtect" of a symbol can be got from `GET /dapi/v1/exchangeInfo`
     * - `STOP`, `STOP_MARKET`:
     * - BUY: latest price ("MARK_PRICE" or "CONTRACT_PRICE") >= `stopPrice`
     * - SELL: latest price ("MARK_PRICE" or "CONTRACT_PRICE")
     * -`TAKE_PROFIT`, `TAKE_PROFIT_MARKET`:
     * - BUY: latest price ("MARK_PRICE" or "CONTRACT_PRICE")
     * - SELL: latest price ("MARK_PRICE" or "CONTRACT_PRICE") >= `stopPrice`
     * - `TRAILING_STOP_MARKET`:
     * - BUY: the lowest price after order placed ``= the lowest price * (1 + `callbackRate`)
     * - SELL: the highest price after order placed >= `activationPrice`, and the latest price
     *
     * - For `TRAILING_STOP_MARKET`, if you got such error code. > `{"code": -2021, "msg": "Order would immediately trigger."}` > means that the parameters you send do not meet the following requirements:
     * - BUY: `activationPrice` should be smaller than latest price.
     * - SELL: `activationPrice` should be larger than latest price.
     * - If `newOrderRespType ` is sent as `RESULT` :
     * - `MARKET` order: the final FILLED result of the order will be return directly.
     * - `LIMIT` order with special `timeInForce`: the final status result of the order(FILLED or EXPIRED) will be returned directly.
     * - `STOP_MARKET`, `TAKE_PROFIT_MARKET` with `closePosition`=`true`:
     * - Follow the same rules for condition orders.
     * - If triggered,**close all** current long position( if `SELL`) or current short position( if `BUY`).
     * - Cannot be used with `quantity` parameter
     * - Cannot be used with `reduceOnly` parameter
     * - In Hedge Mode,cannot be used with `BUY` orders in `LONG` position side. and cannot be used with `SELL` orders in `SHORT` position side
     *
     * - `selfTradePreventionMode` is only effective when `timeInForce` set to `IOC` or `GTC`.
     *
     * @summary New Order (TRADE)
     * @param {NewOrderRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<NewOrderResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/trade#new-order Binance API Documentation}
     */
    newOrder(requestParameters: NewOrderRequest): Promise<RestApiResponse<NewOrderResponse>> {
        return this.tradeApi.newOrder(requestParameters);
    }

    /**
     * Place multiple orders
     *
     * Parameter rules are same with `New Order`
     * Batch orders are processed concurrently, and the order of matching is not guaranteed.
     * The order of returned contents for batch orders is the same as the order of the order list.
     *
     * Weight(IP): 5
     *
     * Security Type: TRADE
     *
     * Notes:
     * - `batchOrders` must be a JSON array of order parameter objects.
     * - Example:
     * `/dapi/v1/batchOrders?batchOrders=[{"type":"LIMIT","timeInForce":"GTC","symbol":"BTCUSD_PERP","side":"BUY","price":"10001","quantity":"1"}]`
     *
     * @summary Place Multiple Orders (TRADE)
     * @param {PlaceMultipleOrdersRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<PlaceMultipleOrdersResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/trade#place-multiple-orders Binance API Documentation}
     */
    placeMultipleOrders(
        requestParameters: PlaceMultipleOrdersRequest
    ): Promise<RestApiResponse<PlaceMultipleOrdersResponse>> {
        return this.tradeApi.placeMultipleOrders(requestParameters);
    }

    /**
     * Query position ADL quantile estimation
     *
     * Values update every 30s.
     * Values 0, 1, 2, 3, 4 shows the queue position and possibility of ADL from low to high.
     * For positions of the symbol are in One-way Mode or isolated margined in Hedge Mode, "LONG", "SHORT", and "BOTH" will be returned to show the positions' adl quantiles of different position sides.
     * If the positions of the symbol are crossed margined in Hedge Mode:
     * "HEDGE" as a sign will be returned instead of "BOTH";
     * A same value caculated on unrealized pnls on long and short sides' positions will be shown for "LONG" and "SHORT" when there are positions in both of long and short sides.
     *
     * Weight(IP): 5
     *
     * Security Type: USER_DATA
     *
     * @summary Position ADL Quantile Estimation (USER_DATA)
     * @param {PositionAdlQuantileEstimationRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<PositionAdlQuantileEstimationResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/trade#position-adl-quantile-estimation Binance API Documentation}
     */
    positionAdlQuantileEstimation(
        requestParameters: PositionAdlQuantileEstimationRequest = {}
    ): Promise<RestApiResponse<PositionAdlQuantileEstimationResponse>> {
        return this.tradeApi.positionAdlQuantileEstimation(requestParameters);
    }

    /**
     * Get current account information.
     *
     * Weight(IP): 1
     *
     * Security Type: USER_DATA
     *
     * Notes:
     * - If neither `marginAsset` nor `pair` is sent, positions of all symbols with `TRADING` status will be returned.
     * - for One-way Mode user, the response will only show the "BOTH" positions
     * - for Hedge Mode user, the response will show "BOTH", "LONG", and "SHORT" positions. **Note** > Please use with user data stream `ACCOUNT_UPDATE` to meet your timeliness and accuracy needs.
     * - Please use with user data stream ACCOUNT_UPDATE to meet your timeliness and accuracy needs.
     *
     * @summary Position Information (USER_DATA)
     * @param {PositionInformationRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<PositionInformationResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/trade#position-information Binance API Documentation}
     */
    positionInformation(
        requestParameters: PositionInformationRequest = {}
    ): Promise<RestApiResponse<PositionInformationResponse>> {
        return this.tradeApi.positionInformation(requestParameters);
    }

    /**
     * Query Current Open Order
     *
     * Weight(IP): 1
     *
     * Security Type: USER_DATA
     *
     * Notes:
     * - Either`orderId` or `origClientOrderId` must be sent
     * - If the queried order has been filled or cancelled, the error message "Order does not exist" will be returned.
     *
     * @summary Query Current Open Order (USER_DATA)
     * @param {QueryCurrentOpenOrderRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<QueryCurrentOpenOrderResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/trade#query-current-open-order Binance API Documentation}
     */
    queryCurrentOpenOrder(
        requestParameters: QueryCurrentOpenOrderRequest
    ): Promise<RestApiResponse<QueryCurrentOpenOrderResponse>> {
        return this.tradeApi.queryCurrentOpenOrder(requestParameters);
    }

    /**
     * Check an order's status.
     *
     * These orders will not be found:
     * order status is CANCELED or EXPIRED AND order has NO filled trade AND created time + 3 days < current time
     * order create time + 90 days < current time
     *
     * Weight(IP): 1
     *
     * Security Type: USER_DATA
     *
     * Notes:
     * - Either `orderId` or `origClientOrderId` must be sent.
     *
     * @summary Query Order (USER_DATA)
     * @param {QueryOrderRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<QueryOrderResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/trade#query-order Binance API Documentation}
     */
    queryOrder(requestParameters: QueryOrderRequest): Promise<RestApiResponse<QueryOrderResponse>> {
        return this.tradeApi.queryOrder(requestParameters);
    }

    /**
     * User's Force Orders
     *
     * Weight: **20** (after CM migration: **20** with symbol / **50** without symbol)
     *
     * Security Type: USER_DATA
     *
     * Notes:
     * - If "autoCloseType" is not sent, orders with both of the types will be returned
     * - Only support querying data in the past 90 days
     *
     * @summary User\'s Force Orders (USER_DATA)
     * @param {UsersForceOrdersRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<UsersForceOrdersResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/trade#users-force-orders Binance API Documentation}
     */
    usersForceOrders(
        requestParameters: UsersForceOrdersRequest = {}
    ): Promise<RestApiResponse<UsersForceOrdersResponse>> {
        return this.tradeApi.usersForceOrders(requestParameters);
    }

    /**
     * Close out a user data stream.
     *
     * Weight(IP): 1
     *
     * Security Type: USER_STREAM
     *
     * @summary Close User Data Stream (USER_STREAM)
     *
     * @returns {Promise<RestApiResponse<void>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/user-data-streams#close-user-data-stream Binance API Documentation}
     */
    closeUserDataStream(): Promise<RestApiResponse<void>> {
        return this.userDataStreamsApi.closeUserDataStream();
    }

    /**
     * Keepalive a user data stream to prevent a time out. User data streams
     * will close after 60 minutes.
     *
     * Weight(IP): 1
     *
     * Security Type: USER_STREAM
     *
     * @summary Keepalive User Data Stream (USER_STREAM)
     *
     * @returns {Promise<RestApiResponse<KeepaliveUserDataStreamResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/user-data-streams#keepalive-user-data-stream Binance API Documentation}
     */
    keepaliveUserDataStream(): Promise<RestApiResponse<KeepaliveUserDataStreamResponse>> {
        return this.userDataStreamsApi.keepaliveUserDataStream();
    }

    /**
     * Start a new user data stream. The stream will close after 60 minutes
     * unless a keepalive is sent. If the account has an active `listenKey`,
     * that `listenKey` will be returned and its validity will be extended for
     * 60 minutes.
     *
     * Weight(IP): 1
     *
     * Security Type: USER_STREAM
     *
     * @summary Start User Data Stream (USER_STREAM)
     *
     * @returns {Promise<RestApiResponse<StartUserDataStreamResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/user-data-streams#start-user-data-stream Binance API Documentation}
     */
    startUserDataStream(): Promise<RestApiResponse<StartUserDataStreamResponse>> {
        return this.userDataStreamsApi.startUserDataStream();
    }
}
